/**
 * LA RELECTURE DES DERNIERS MESSAGES DE TEST D'UN DESTINATAIRE (LOT F4).
 * ============================================================================
 *
 * INTENTION : sur la fiche d'une surveillance, le client relit ce que sont
 * devenus les derniers messages de test envoyés à une personne, en toutes
 * lettres, sans aucun chiffre de son numéro.
 *
 * La route 6 bis est lue AU CLIC de la personne, une lecture par clic : jamais
 * au chargement de la fiche, jamais en boucle, aucun minuteur. Un second clic
 * pendant qu'une lecture est en vol pour le même destinataire ne relance rien.
 *
 * Tenu par session (personne et entreprise), par surveillance et par
 * destinataire : une fin de lecture qui n'est plus la courante est jetée sans
 * toucher à l'état, en succès comme en échec.
 */
import { defineStore } from "pinia"
import { ref } from "vue"
import { cleDeSession } from "@modules/alarmes-v2/stores/ouverture.store"
import { serviceAlarmesV2 } from "@modules/alarmes-v2/services/alarmes-v2.service"
import {
    CODE_INDISPONIBLE,
    ErreurAlarmesV2,
    versErreurAlarmesV2,
} from "@modules/alarmes-v2/services/appel"
import type { AlarmesV2LigneTest } from "@modules/alarmes-v2/types/alarmes-v2"

/** Le nombre de messages de test relus par destinataire. [PROPOSITION] */
export const LIMITE_TESTS = 3

/** Où en est la relecture pour un destinataire. */
export type StatutResultat = "chargement" | "pret" | "erreur"

/** La relecture pour un destinataire. */
export interface ResultatTest {
    statut: StatutResultat
    lignes: AlarmesV2LigneTest[]
    erreur: ErreurAlarmesV2 | null
}

const cleDe = (idInstance: string, idUser: string) => `${idInstance}|${idUser}`

export const useResultatsTestStore = defineStore(
    "alarmesV2ResultatsTest",
    () => {
        /** Les relectures, par surveillance et destinataire. */
        const parDestinataire = ref<Record<string, ResultatTest>>({})

        /** La session à laquelle appartiennent les relectures. */
        let sessionLue = ""

        /** Le numéro de la lecture courante, par destinataire. */
        const courantes = new Map<string, number>()
        let compteur = 0

        /** Vide tout si la session a changé depuis la dernière relecture. */
        function suivreLaSession(): string {
            const session = cleDeSession()
            if (session !== sessionLue) {
                parDestinataire.value = {}
                courantes.clear()
                sessionLue = session
            }
            return session
        }

        /**
         * La relecture d'un destinataire, ou `null` si elle n'a pas été demandée
         * dans la session courante.
         */
        function resultat(
            idInstance: string,
            idUser: string
        ): ResultatTest | null {
            // Lu d'abord : la dépendance réactive existe avant toute relecture.
            const lu = parDestinataire.value[cleDe(idInstance, idUser)] ?? null
            return cleDeSession() === sessionLue ? lu : null
        }

        /**
         * Relit les derniers messages de test d'un destinataire : UNE lecture.
         * Ne rejette jamais.
         * @param idInstance - la surveillance
         * @param idUser - le destinataire
         */
        async function relire(
            idInstance: string,
            idUser: string
        ): Promise<void> {
            const session = suivreLaSession()
            if (!session || !idInstance || !idUser) return
            const cle = cleDe(idInstance, idUser)
            if (parDestinataire.value[cle]?.statut === "chargement") return

            const numero = ++compteur
            courantes.set(cle, numero)
            parDestinataire.value = {
                ...parDestinataire.value,
                [cle]: { statut: "chargement", lignes: [], erreur: null },
            }
            const estCourante = () => {
                if (cleDeSession() !== session) {
                    // La session a changé : ses relectures ne se montreront plus
                    // jamais, aucune ne reste « en cours » pour un retour à elle.
                    if (sessionLue === session) reinitialiser()
                    return false
                }
                return courantes.get(cle) === numero
            }
            const poser = (r: ResultatTest) => {
                parDestinataire.value = { ...parDestinataire.value, [cle]: r }
            }
            try {
                const reponse = await serviceAlarmesV2.alarmesV2LireTests(
                    idInstance,
                    idUser,
                    LIMITE_TESTS
                )
                if (!estCourante()) return
                if (!reponse || !Array.isArray(reponse.items)) {
                    // Une réponse illisible n'est pas « aucun message de test ».
                    poser({
                        statut: "erreur",
                        lignes: [],
                        erreur: new ErreurAlarmesV2({
                            code: CODE_INDISPONIBLE,
                            statut: null,
                        }),
                    })
                    return
                }
                poser({
                    statut: "pret",
                    lignes: reponse.items.filter(
                        (l): l is AlarmesV2LigneTest =>
                            l !== null && typeof l === "object"
                    ),
                    erreur: null,
                })
            } catch (e) {
                if (!estCourante()) return
                poser({
                    statut: "erreur",
                    lignes: [],
                    erreur:
                        e instanceof ErreurAlarmesV2
                            ? e
                            : versErreurAlarmesV2(e),
                })
            }
        }

        /** Remet le store à son état de départ ; toute lecture en vol est jetée. */
        function reinitialiser(): void {
            parDestinataire.value = {}
            courantes.clear()
            sessionLue = ""
        }

        return { parDestinataire, resultat, relire, reinitialiser }
    }
)
